import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useAdminApi } from '@/hooks/useAdminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Trash2, UserPlus, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface WaitlistEntry {
  id: string;
  email: string;
  status: 'pending' | 'invited' | 'converted';
  invited_at: string | null;
  created_at: string;
}

import { Badge } from '@/components/ui/badge';
import { Clock } from 'lucide-react';

export default function AdminWaitlist() {
  const { getWaitlist, deleteWaitlistEntry, createUser, isLoading } = useAdminApi();
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<WaitlistEntry | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Invite form states
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteStorageLimit, setInviteStorageLimit] = useState(100);
  const [inviteAiLimit, setInviteAiLimit] = useState(100);

  const fetchWaitlist = async () => {
    try {
      const data = await getWaitlist();
      setWaitlist(data);
    } catch (error) {
      console.error('Failed to fetch waitlist:', error);
      toast.error('Failed to load waitlist');
    }
  };

  useEffect(() => {
    fetchWaitlist();
  }, []);

  const filteredWaitlist = waitlist.filter(entry =>
    entry.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleInvite = async () => {
    if (!selectedEntry || !invitePassword) {
      toast.error('Password is required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Create user with fromWaitlist=true to send special welcome email
      await createUser(selectedEntry.email, invitePassword, inviteStorageLimit, inviteAiLimit, true, true);
      
      setIsInviteOpen(false);
      setInvitePassword('');
      setInviteStorageLimit(100);
      setInviteAiLimit(100);
      fetchWaitlist();
      toast.success(`Invited ${selectedEntry.email} - Welcome email sent!`);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to invite user';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;
    
    setIsSubmitting(true);
    try {
      await deleteWaitlistEntry(selectedEntry.id);
      setIsDeleteOpen(false);
      fetchWaitlist();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete entry';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportToCsv = () => {
    const csvContent = [
      ['Email', 'Status', 'Signed Up', 'Invited At'],
      ...waitlist.map(entry => [
        entry.email,
        entry.status,
        format(new Date(entry.created_at), 'yyyy-MM-dd HH:mm:ss'),
        entry.invited_at ? format(new Date(entry.invited_at), 'yyyy-MM-dd HH:mm:ss') : ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mindfull-waitlist-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openInviteDialog = (entry: WaitlistEntry) => {
    setSelectedEntry(entry);
    setInvitePassword(generatePassword());
    setInviteStorageLimit(100);
    setInviteAiLimit(100);
    setIsInviteOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Waitlist</h1>
            <p className="text-muted-foreground">{waitlist.length} people waiting</p>
          </div>
          <Button variant="outline" onClick={exportToCsv}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search waitlist..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Waitlist Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Signed Up</TableHead>
                <TableHead>Invited</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>
                    </TableRow>
                  ))}
                </>
              ) : filteredWaitlist.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {searchQuery ? 'No entries match your search' : 'Waitlist is empty'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredWaitlist.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">{entry.email}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={
                          entry.status === 'converted' ? 'default' :
                          entry.status === 'invited' ? 'secondary' :
                          'outline'
                        }
                      >
                        {entry.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(entry.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {entry.invited_at ? format(new Date(entry.invited_at), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {entry.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openInviteDialog(entry)}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            Invite
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedEntry(entry);
                            setIsDeleteOpen(true);
                          }}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Invite Dialog */}
      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Create an account for {selectedEntry?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="flex gap-2">
                <Input
                  id="password"
                  type="text"
                  value={invitePassword}
                  onChange={(e) => setInvitePassword(e.target.value)}
                  placeholder="Enter password"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setInvitePassword(generatePassword())}
                >
                  Generate
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Share this password securely with the user
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="storageLimit">Storage Limit (MB)</Label>
                <Input
                  id="storageLimit"
                  type="number"
                  value={inviteStorageLimit}
                  onChange={(e) => setInviteStorageLimit(parseInt(e.target.value) || 100)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="aiLimit">AI Requests Limit</Label>
                <Input
                  id="aiLimit"
                  type="number"
                  value={inviteAiLimit}
                  onChange={(e) => setInviteAiLimit(parseInt(e.target.value) || 100)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsInviteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={isSubmitting || !invitePassword}>
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from Waitlist</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{selectedEntry?.email}</strong> from the waitlist?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
